import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, PenLine } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function Stars({ value, size = "h-4 w-4", onSelect, interactive = false }) {
  const [hover, setHover] = useState(0);
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type={interactive ? "button" : undefined}
          disabled={!interactive}
          onMouseEnter={() => interactive && setHover(n)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onSelect?.(n)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          data-testid={interactive ? `star-${n}` : undefined}
        >
          <Star className={`${size} ${(hover || value) >= n ? "fill-gold text-gold" : "text-sand"}`} />
        </button>
      ))}
    </span>
  );
}

export function ProductReviews({ slug }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return toast.error("Please add your name and review");
    if (rating < 1) return toast.error("Please select a star rating");
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, { name, rating, title, body });
      toast.success("Thank you — your review is live!");
      setName(""); setRating(0); setTitle(""); setBody(""); setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return null;
  const { reviews, average, count, distribution } = data;

  return (
    <section className="border-t border-[#E4DDD0] bg-cream-2" data-testid="product-reviews">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-3">Loved by real skin</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-moss">Customer Reviews</h2>

        <div className="mt-10 grid gap-12 lg:grid-cols-[320px_1fr]">
          {/* summary */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-end gap-3">
              <span className="font-serif text-6xl text-moss" data-testid="reviews-average">{average || "—"}</span>
              <div className="pb-2">
                <Stars value={Math.round(average)} />
                <p className="mt-1 text-sm text-muted" data-testid="reviews-count">{count} review{count === 1 ? "" : "s"}</p>
              </div>
            </div>
            <div className="mt-6 space-y-1.5">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = distribution[String(n)] || 0;
                const pct = count ? (c / count) * 100 : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs text-muted">
                    <span className="w-3">{n}</span>
                    <Star className="h-3 w-3 fill-gold text-gold" />
                    <div className="h-2 flex-1 rounded-full bg-cream-3 overflow-hidden">
                      <div className="h-full rounded-full bg-gold" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right">{c}</span>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="btn-gold mt-8 flex w-full items-center justify-center gap-2 rounded-sm py-3 text-xs font-semibold uppercase tracking-wider"
              data-testid="write-review-toggle"
            >
              <PenLine className="h-4 w-4" /> {showForm ? "Close" : "Write a Review"}
            </button>
          </div>

          {/* list + form */}
          <div>
            {showForm && (
              <motion.form
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={submit}
                className="mb-10 rounded-sm border border-[#E4DDD0] bg-cream p-6"
                data-testid="review-form"
              >
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">Your rating</p>
                <Stars value={rating} size="h-7 w-7" interactive onSelect={setRating} />
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                    className="rounded-sm border border-[#E4DDD0] bg-cream-2 px-3 py-2 text-sm focus:border-gold focus:outline-none" data-testid="review-name" />
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
                    className="rounded-sm border border-[#E4DDD0] bg-cream-2 px-3 py-2 text-sm focus:border-gold focus:outline-none" data-testid="review-title" />
                </div>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Tell us how it worked for your skin…" rows={4}
                  className="mt-3 w-full rounded-sm border border-[#E4DDD0] bg-cream-2 px-3 py-2 text-sm focus:border-gold focus:outline-none" data-testid="review-body" />
                <button type="submit" disabled={submitting}
                  className="btn-gold mt-4 flex items-center gap-2 rounded-sm px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-60" data-testid="review-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit Review
                </button>
              </motion.form>
            )}

            {reviews.length === 0 ? (
              <p className="text-muted" data-testid="reviews-empty">Be the first to review this butter.</p>
            ) : (
              <div className="space-y-6">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b border-[#E4DDD0] pb-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <Stars value={r.rating} />
                      <span className="font-mono text-xs text-muted">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    {r.title && <h3 className="mt-3 font-serif text-xl text-moss">{r.title}</h3>}
                    <p className="mt-2 text-muted leading-relaxed">{r.body}</p>
                    <p className="mt-3 text-sm text-moss">
                      {r.name}
                      {r.verified && <span className="ml-2 rounded-full bg-moss/10 px-2 py-0.5 font-mono text-[10px] uppercase text-moss">Verified</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
